/**
 * Module-level bridge so non-React code (baseQuery, BaseAPI interceptors) can
 * raise user-facing toasts. ToastProvider registers on mount — the same
 * pattern as registerWebToastManager in utils/common/alert.ts (spec §3.4).
 */
export interface GlobalToast {
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
}

type ToastHandler = (toast: GlobalToast) => void;

let handler: ToastHandler | undefined;
const lastEmitByKey = new Map<string, number>();

export const registerGlobalToastHandler = (fn: ToastHandler): void => {
  handler = fn;
};

export const unregisterGlobalToastHandler = (fn: ToastHandler): void => {
  if (handler === fn) handler = undefined;
};

export const emitGlobalToast = (toast: GlobalToast): void => {
  handler?.(toast);
};

/**
 * Deduped variant for high-frequency sources (many parallel queries failing
 * through the same gateway): at most one toast per key per window.
 * Generic emitGlobalToast is never deduped.
 */
export const emitDedupedToast = (key: string, toast: GlobalToast, windowMs = 10_000): void => {
  const now = Date.now();
  const last = lastEmitByKey.get(key) ?? 0;
  if (now - last < windowMs) return;
  lastEmitByKey.set(key, now);
  emitGlobalToast(toast);
};

export const __resetToastBridgeForTests = (): void => {
  handler = undefined;
  lastEmitByKey.clear();
};
